/**
 * A preview of an invoice CSV before it is sent: the same columns and rules the hub applies
 * (apps/hub/src/ext/finance/validate.ts), so the person sees problems before importing. The
 * hub checks everything again.
 */
import type { CostEntry, EntryCategory, Id } from '@orgus/protocol';
import { ENTRY_CATEGORIES } from '@orgus/protocol';

export function parseCsv(src: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cur = '';
  let q = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (q) {
      if (ch === '"' && src[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') q = false;
      else cur += ch;
      continue;
    }
    if (ch === '"') q = true;
    else if (ch === ',') {
      row.push(cur);
      cur = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(cur);
      cur = '';
      if (row.some((c) => c.trim() !== '')) rows.push(row);
      row = [];
    } else cur += ch;
  }
  row.push(cur);
  if (row.some((c) => c.trim() !== '')) rows.push(row);
  return rows;
}

const ALIASES: Record<string, string> = {
  date: 'from',
  from: 'from',
  start: 'from',
  period_start: 'from',
  to: 'to',
  end: 'to',
  period_end: 'to',
  vendor: 'vendor',
  supplier: 'vendor',
  description: 'description',
  memo: 'description',
  amount: 'amount',
  amount_usd: 'amount',
  total: 'amount',
  category: 'category',
  project: 'project',
  invoice: 'invoiceNumber',
  invoice_number: 'invoiceNumber',
  number: 'invoiceNumber',
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;

function isDay(s: string): boolean {
  if (!DAY.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y!, m! - 1, d!);
  return dt.getFullYear() === y && dt.getMonth() === m! - 1 && dt.getDate() === d;
}

export function previewInvoiceCsv(src: string, projects: { id: Id; name: string }[]): { ok: true; entries: CostEntry[] } | { ok: false; why: string } {
  const rows = parseCsv(src.replace(/^﻿/, ''));
  if (rows.length < 2) return { ok: false, why: 'The file needs a header row and at least one invoice row.' };
  const head = rows[0]!.map((h) => ALIASES[h.trim().toLowerCase().replace(/[\s-]+/g, '_')] ?? '');
  for (const need of ['from', 'vendor', 'amount']) if (!head.includes(need)) return { ok: false, why: `The header has no ${need === 'from' ? 'date' : need} column.` };
  if (rows.length - 1 > 1000) return { ok: false, why: 'Import at most 1000 rows at a time.' };
  const problems: string[] = [];
  const entries: CostEntry[] = [];
  const now = Date.now();
  rows.slice(1).forEach((r, idx) => {
    const line = idx + 2;
    const v: Record<string, string> = {};
    head.forEach((h, i) => {
      if (h) v[h] = (r[i] ?? '').trim();
    });
    const category = (v.category?.toLowerCase() || 'platform') as EntryCategory;
    const amount = Number((v.amount ?? '').replace(/[$,\s]/g, ''));
    const from = v.from ?? '';
    const to = v.to || from;
    let projectId: Id | null = null;
    if (v.project) {
      const p = projects.find((x) => x.id === v.project) ?? projects.find((x) => x.name.toLowerCase() === v.project!.toLowerCase());
      if (!p) return void problems.push(`row ${line}: no project named ${v.project}`);
      projectId = p.id;
    }
    if (!ENTRY_CATEGORIES.includes(category)) return void problems.push(`row ${line}: category must be one of ${ENTRY_CATEGORIES.join(', ')}`);
    if (!v.vendor) return void problems.push(`row ${line}: no vendor`);
    if (!Number.isFinite(amount) || amount <= 0) return void problems.push(`row ${line}: the amount is not a number above 0`);
    if (!isDay(from) || !isDay(to) || to < from) return void problems.push(`row ${line}: dates must look like 2026-10-01, the end on or after the start`);
    entries.push({
      id: `fe_import_${now.toString(36)}_${idx}`,
      category,
      vendor: v.vendor,
      description: v.description ?? '',
      amount: Math.round(amount * 100) / 100,
      from,
      to,
      projectId,
      source: 'invoice',
      ...(v.invoiceNumber ? { invoiceNumber: v.invoiceNumber } : {}),
      createdBy: 'import',
      createdAt: now,
      updatedAt: now,
    });
  });
  if (problems.length) return { ok: false, why: `${problems.length} ${problems.length === 1 ? 'row has a problem' : 'rows have problems'}: ${problems.slice(0, 3).join('; ')}${problems.length > 3 ? '; and more' : ''}.` };
  return { ok: true, entries };
}
